import React, { useMemo, useState } from 'react';
import type { JSX } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, ChevronDown, Crosshair, Layers, Map as PlanSymbol, Plus, Trash2 } from 'lucide-react';
import type { ProjectPlan, ProjektVerortung, VerortungGeruest, VerortungPlan } from '../../types';
import { geruestLagen, geschosseLesen, geschossNameNormalisieren, planGeoreferenz } from '../../services/verortung';
import { GeoreferenzStatus, PlanKalibrierung } from './PlanKalibrierung';
import {
    GERUEST_ABSTAND_MAX_M, GERUEST_ABSTAND_MIN_M, GERUEST_LAGEN_MAX, GERUEST_LAGEN_MIN, STANDARD_GERUEST, ganzzahlLesen,
    geschossAendern, geschosseImportieren, geschossLoeschen, geschossNameLesen, hoeheLesen, istGeoreferenz, lagenVorschau,
    neuesGeschoss, planArtSetzen, plaeneMitGeschoss, planSetzen, zahlImBereichLesen, zahlText,
} from './einrichtungLogik';

/**
 * Projekt-Einrichtung der Sensor-Verortung (docs/verortung-kamera-vertrag.md,
 * Paket C; Plan 9.2): Projektschalter, Geschosshöhen, Gerüst, Plan-Art und
 * Geschoss je Plan, Georeferenz (Vollbild `PlanKalibrierung`).
 *
 * Abgelegt wird über `onChange` in `report.metadata.verortung` — nie an
 * `ProjectPlan` (project_plans hat feste Spalten). Ohne `onChange` nur Ansicht.
 * Einklappbar, standardmäßig zu: die Karte liegt unter den Plänen und ist
 * für die meisten Projekte nicht nötig.
 */

export interface VerortungEinrichtungProps {
    /** Bereits über `verortungLesen` normalisiert. */
    verortung: ProjektVerortung;
    plaene: readonly ProjectPlan[];
    /** Fehlt → nur lesen (Nur-Ansicht-Projekt). */
    onChange?: (v: ProjektVerortung) => void;
    /** Karte beim ersten Anzeigen aufgeklappt (Tests, Direktsprung). Standard: zu. */
    defaultOffen?: boolean;
}

// ─── Kleine Bausteine ────────────────────────────────────────────────────────

/**
 * Textfeld mit Entwurf: getippt wird lokal, jeder lesbare Stand geht sofort
 * über `onWert` hinaus (auch „3,“ → 3), ein unlesbarer bleibt nur im Feld und
 * zeigt `fehlerText`. Beim Verlassen springt die Anzeige auf den gespeicherten
 * Wert zurück. Ohne `onWert` deaktiviert.
 */
function EntwurfFeld<T>({ wert, lesen, onWert, fehlerText, label, inputMode, einheit, className }: {
    wert: string;
    lesen: (text: string) => T | null;
    onWert?: (w: T) => void;
    fehlerText: string;
    label: string;
    inputMode?: 'text' | 'decimal' | 'numeric';
    einheit?: string;
    className?: string;
}): JSX.Element {
    const [entwurf, setEntwurf] = useState<string | null>(null);
    const ungueltig = entwurf !== null && lesen(entwurf) === null;
    return (
        <div className={className}>
            <div className="relative">
                <input
                    type="text"
                    inputMode={inputMode}
                    value={entwurf ?? wert}
                    disabled={!onWert}
                    aria-label={label}
                    aria-invalid={ungueltig}
                    onChange={(e) => {
                        const text = e.target.value;
                        setEntwurf(text);
                        const w = lesen(text);
                        if (w !== null) onWert?.(w);
                    }}
                    onBlur={() => setEntwurf(null)}
                    className={`w-full rounded-lg border px-3 py-2 text-sm bg-white disabled:bg-slate-50 disabled:text-slate-500 ${einheit ? 'pr-8' : ''} ${ungueltig ? 'border-rose-400' : 'border-slate-300'}`}
                />
                {einheit && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none">{einheit}</span>}
            </div>
            {ungueltig && <p className="mt-0.5 text-[11px] text-rose-700">{fehlerText}</p>}
        </div>
    );
}

function Schalter({ an, onUmschalten, label }: { an: boolean; onUmschalten?: () => void; label: string }): JSX.Element {
    return (
        <button
            type="button"
            role="switch"
            aria-checked={an}
            aria-label={label}
            onClick={onUmschalten}
            disabled={!onUmschalten}
            className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors disabled:opacity-60 ${an ? 'bg-sky-600' : 'bg-slate-300'}`}
        >
            <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${an ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
    );
}

const Abschnitt: React.FC<{ titel: string; symbol: React.ReactNode; children: React.ReactNode }> = ({ titel, symbol, children }) => (
    <div className="rounded-xl border border-slate-200 p-3">
        <p className="text-sm font-semibold text-slate-800 flex items-center gap-2">{symbol}{titel}</p>
        {children}
    </div>
);

// ─── Karte ───────────────────────────────────────────────────────────────────

export function VerortungEinrichtung({ verortung, plaene, onChange, defaultOffen = false }: VerortungEinrichtungProps): JSX.Element {
    const [offen, setOffen] = useState(defaultOffen);
    const [kalibrierPlanId, setKalibrierPlanId] = useState<string | null>(null);
    const [importText, setImportText] = useState('');
    const [meldung, setMeldung] = useState<string | null>(null);
    /** Letzte Gerüst-Angabe vor dem Ausschalten — beim Wiedereinschalten zurück. */
    const [geruestVorher, setGeruestVorher] = useState<VerortungGeruest | null>(null);

    const aendern = onChange;
    const lagen = useMemo(() => geruestLagen(verortung.geruest), [verortung.geruest]);
    const importiert = useMemo(() => geschosseLesen(importText), [importText]);
    const planStatus = useMemo(() => plaene.map((p) => {
        const k = verortung.plaene?.[p.id];
        return { plan: p, k, geo: planGeoreferenz(k) };
    }), [plaene, verortung.plaene]);
    const georeferenziert = planStatus.filter((s) => istGeoreferenz(s.geo)).length;
    const doppelte = useMemo(() => {
        const gesehen = new Set<string>();
        const doppelt = new Set<string>();
        for (const g of verortung.geschosse) {
            const n = geschossNameNormalisieren(g.name);
            if (gesehen.has(n)) doppelt.add(g.name);
            gesehen.add(n);
        }
        return [...doppelt];
    }, [verortung.geschosse]);

    const zusammenfassung = !verortung.aktiv
        ? 'Im Projekt ausgeschaltet'
        : [
            verortung.geschosse.length ? `${verortung.geschosse.length} Geschoss${verortung.geschosse.length === 1 ? '' : 'e'}` : 'keine Geschosse',
            lagen.length ? `Gerüst ${lagen.length} Lagen` : null,
            plaene.length ? `${georeferenziert} von ${plaene.length} Plänen georeferenziert` : null,
        ].filter(Boolean).join(' · ');

    const kalibrierPlan = kalibrierPlanId ? plaene.find((p) => p.id === kalibrierPlanId) ?? null : null;

    // ─── Änderungen ──────────────────────────────────────────────────────────

    const geschossLoeschenKlick = (id: string, name: string) => {
        if (!aendern) return;
        const n = plaeneMitGeschoss(verortung, id);
        aendern(geschossLoeschen(verortung, id));
        setMeldung(n > 0 ? `„${name}“ gelöscht — Zuordnung bei ${n} Plan${n === 1 ? '' : 'en'} entfernt.` : `„${name}“ gelöscht.`);
    };

    const geschossHinzufuegen = () => {
        if (!aendern) return;
        aendern({ ...verortung, geschosse: [...verortung.geschosse, neuesGeschoss(verortung.geschosse, crypto.randomUUID())] });
        setMeldung(null);
    };

    const importieren = (modus: 'ersetzen' | 'ergaenzen') => {
        if (!aendern || importiert.geschosse.length === 0) return;
        aendern(geschosseImportieren(verortung, importiert.geschosse, modus, () => crypto.randomUUID()));
        const n = importiert.geschosse.length;
        setImportText('');
        setMeldung(`${n} Geschoss${n === 1 ? '' : 'e'} ${modus === 'ersetzen' ? 'übernommen (Liste ersetzt)' : 'ergänzt'}.`);
    };

    const geruestUmschalten = () => {
        if (!aendern) return;
        if (verortung.geruest) {
            setGeruestVorher(verortung.geruest);
            aendern({ ...verortung, geruest: null });
        } else {
            aendern({ ...verortung, geruest: geruestVorher ?? STANDARD_GERUEST });
        }
    };

    const geruestFeld = (patch: Partial<VerortungGeruest>) => {
        if (!aendern || !verortung.geruest) return;
        aendern({ ...verortung, geruest: { ...verortung.geruest, ...patch } });
    };

    const planAendern = (planId: string, k: VerortungPlan) => {
        if (!aendern) return;
        aendern(planSetzen(verortung, planId, k));
    };

    const g = verortung.geruest ?? null;

    const kalibrierAnsicht = kalibrierPlan ? (
        <PlanKalibrierung
            plan={kalibrierPlan}
            verortung={verortung}
            plaene={plaene}
            onChange={aendern ? (k) => aendern(planSetzen(verortung, kalibrierPlan.id, k)) : undefined}
            onSchliessen={() => setKalibrierPlanId(null)}
        />
    ) : null;
    // Ans Dokument hängen: ein Vorfahr mit transform/filter würde `fixed inset-0` sonst einfangen.
    const kalibrierPortal = kalibrierAnsicht && typeof document !== 'undefined' ? createPortal(kalibrierAnsicht, document.body) : kalibrierAnsicht;

    return (
        <section className="bg-white rounded-2xl border border-slate-200 shadow-sm">
            <button
                type="button"
                onClick={() => setOffen((o) => !o)}
                aria-expanded={offen}
                className="w-full flex items-center justify-between gap-3 p-4 text-left"
            >
                <span className="flex items-center gap-2 min-w-0">
                    <Crosshair className="w-4 h-4 text-sky-600 shrink-0" />
                    <span className="min-w-0">
                        <span className="block text-sm font-bold text-slate-800">Sensor-Verortung (Kamera-Einblendung)</span>
                        <span className="block text-xs text-slate-500 truncate">{zusammenfassung}</span>
                    </span>
                </span>
                <ChevronDown className={`w-4 h-4 text-slate-500 shrink-0 transition-transform ${offen ? 'rotate-180' : ''}`} />
            </button>

            {offen && (
                <div className="px-4 pb-4 space-y-4">
                    {!aendern && (
                        <p className="text-xs rounded-lg px-2.5 py-2 bg-amber-50 text-amber-900 border border-amber-200 flex items-start gap-1.5">
                            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                            <span>Nur Ansicht — Änderungen sind in diesem Projekt nicht möglich.</span>
                        </p>
                    )}
                    {meldung && (
                        <p className="text-sm rounded-lg bg-slate-800 text-white px-3 py-2 flex items-start gap-2" role="status">
                            <span className="flex-1">{meldung}</span>
                            <button type="button" onClick={() => setMeldung(null)} className="text-xs underline shrink-0">OK</button>
                        </p>
                    )}

                    {/* ─── Projektschalter ─────────────────────────────── */}
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <p className="text-sm font-semibold text-slate-800">Im Projekt aktiv</p>
                            <p className="text-xs text-slate-500 mt-0.5">
                                Aus: Die Kamera zeigt in diesem Projekt keine Einblendung und keinen Ortsvorschlag — Fotos wie bisher.
                                Die Sensoren gibt es nur in der Android-App; im Browser ändert sich nichts.
                            </p>
                        </div>
                        <Schalter
                            an={verortung.aktiv}
                            label="Sensor-Verortung im Projekt aktiv"
                            onUmschalten={aendern ? () => aendern({ ...verortung, aktiv: !verortung.aktiv }) : undefined}
                        />
                    </div>

                    {/* ─── Geschosse ────────────────────────────────────── */}
                    <Abschnitt titel="Geschosse" symbol={<Layers className="w-4 h-4 text-sky-600" />}>
                        <p className="text-xs text-slate-500 mt-0.5">Höhe in m relativ ±0,00 (OKFF EG), aus dem Schnitt — z. B. UG −3,20.</p>
                        {verortung.geschosse.length === 0 ? (
                            <p className="mt-2 text-xs text-slate-500 italic">Noch keine Geschosse — hinzufügen oder aus Text übernehmen.</p>
                        ) : (
                            <ul className="mt-2 space-y-2">
                                {verortung.geschosse.map((gs) => (
                                    <li key={gs.id} className="flex items-start gap-2">
                                        <EntwurfFeld
                                            className="flex-1 min-w-0"
                                            wert={gs.name}
                                            lesen={geschossNameLesen}
                                            onWert={aendern ? (name) => aendern(geschossAendern(verortung, gs.id, { name })) : undefined}
                                            fehlerText="Name fehlt"
                                            label={`Name des Geschosses ${gs.name}`}
                                        />
                                        <EntwurfFeld
                                            className="w-28 shrink-0"
                                            wert={zahlText(gs.hoeheM, 2)}
                                            lesen={hoeheLesen}
                                            onWert={aendern ? (hoeheM) => aendern(geschossAendern(verortung, gs.id, { hoeheM })) : undefined}
                                            fehlerText="z. B. -3,20"
                                            label={`Höhe ${gs.name} in Metern`}
                                            inputMode="text"
                                            einheit="m"
                                        />
                                        {aendern && (
                                            <button
                                                type="button"
                                                onClick={() => geschossLoeschenKlick(gs.id, gs.name)}
                                                aria-label={`Geschoss ${gs.name} löschen`}
                                                title="Löschen"
                                                className="shrink-0 rounded-lg p-2.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                        {doppelte.length > 0 && (
                            <p className="mt-2 text-xs text-amber-800">Doppelter Name: {doppelte.join(', ')} — bitte eindeutig benennen.</p>
                        )}
                        {aendern && (
                            <>
                                <button
                                    type="button"
                                    onClick={geschossHinzufuegen}
                                    className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 text-slate-700 active:bg-slate-100 text-sm font-semibold px-4 py-2.5"
                                >
                                    <Plus className="w-4 h-4" /> Geschoss hinzufügen
                                </button>
                                <details className="mt-2">
                                    <summary className="cursor-pointer text-xs font-semibold text-sky-700 py-1">Aus Text übernehmen (z. B. aus dem Schnitt)</summary>
                                    <textarea
                                        value={importText}
                                        onChange={(e) => setImportText(e.target.value)}
                                        rows={4}
                                        placeholder={'Eine Zeile je Geschoss oder mit „;“ getrennt:\nUG -3,20\nEG 0\n1. OG 3,05\n2. OG 6,10'}
                                        aria-label="Geschosse als Text"
                                        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
                                    />
                                    {importText.trim() && (
                                        <p className="text-xs text-slate-600">
                                            {importiert.geschosse.length} Geschoss{importiert.geschosse.length === 1 ? '' : 'e'} erkannt
                                            {importiert.geschosse.length > 0 && `: ${importiert.geschosse.map((x) => x.name).join(', ')}`}
                                        </p>
                                    )}
                                    {importiert.fehler.map((f) => <p key={f} className="text-xs text-rose-700">{f}</p>)}
                                    <div className="mt-2 grid grid-cols-2 gap-2">
                                        <button
                                            type="button"
                                            onClick={() => importieren('ersetzen')}
                                            disabled={importiert.geschosse.length === 0}
                                            className="rounded-lg bg-slate-800 disabled:bg-slate-300 text-white text-xs font-semibold px-3 py-2.5"
                                        >
                                            Liste ersetzen
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => importieren('ergaenzen')}
                                            disabled={importiert.geschosse.length === 0}
                                            className="rounded-lg border border-slate-300 disabled:text-slate-300 text-slate-700 text-xs font-semibold px-3 py-2.5"
                                        >
                                            Ergänzen
                                        </button>
                                    </div>
                                    <p className="mt-1 text-[11px] text-slate-500">
                                        Gleichnamige Geschosse behalten ihre Plan-Zuordnung und übernehmen die neue Höhe.
                                    </p>
                                </details>
                            </>
                        )}
                    </Abschnitt>

                    {/* ─── Gerüst ───────────────────────────────────────── */}
                    <Abschnitt titel="Gerüst" symbol={<Layers className="w-4 h-4 text-orange-500" />}>
                        <div className="mt-0.5 flex items-start justify-between gap-3">
                            <p className="text-xs text-slate-500">
                                Gerüstlagen als Höhe relativ ±0,00 — die Kamera kann dann „Gerüst 4. Lage“ einblenden.
                            </p>
                            <Schalter an={!!g} label="Gerüst vorhanden" onUmschalten={aendern ? geruestUmschalten : undefined} />
                        </div>
                        {g && (
                            <>
                                <div className="mt-2 grid grid-cols-3 gap-2">
                                    <div>
                                        <span className="block text-[11px] font-semibold text-slate-500">Erste Lage</span>
                                        <EntwurfFeld
                                            className="mt-0.5"
                                            wert={zahlText(g.ersteLageM, 2)}
                                            lesen={hoeheLesen}
                                            onWert={aendern ? (ersteLageM) => geruestFeld({ ersteLageM }) : undefined}
                                            fehlerText="z. B. 2,00"
                                            label="Höhe der ersten Gerüstlage in Metern"
                                            inputMode="text"
                                            einheit="m"
                                        />
                                    </div>
                                    <div>
                                        <span className="block text-[11px] font-semibold text-slate-500">Abstand</span>
                                        <EntwurfFeld
                                            className="mt-0.5"
                                            wert={zahlText(g.abstandM, 2)}
                                            lesen={(t) => zahlImBereichLesen(t, GERUEST_ABSTAND_MIN_M, GERUEST_ABSTAND_MAX_M)}
                                            onWert={aendern ? (abstandM) => geruestFeld({ abstandM }) : undefined}
                                            fehlerText="0,5–5 m"
                                            label="Abstand der Gerüstlagen in Metern"
                                            inputMode="decimal"
                                            einheit="m"
                                        />
                                    </div>
                                    <div>
                                        <span className="block text-[11px] font-semibold text-slate-500">Lagen</span>
                                        <EntwurfFeld
                                            className="mt-0.5"
                                            wert={String(g.lagen)}
                                            lesen={(t) => ganzzahlLesen(t, GERUEST_LAGEN_MIN, GERUEST_LAGEN_MAX)}
                                            onWert={aendern ? (anzahl) => geruestFeld({ lagen: anzahl }) : undefined}
                                            fehlerText="1–40"
                                            label="Anzahl der Gerüstlagen"
                                            inputMode="numeric"
                                        />
                                    </div>
                                </div>
                                <p className="mt-2 text-xs text-slate-700">
                                    {lagen.length > 0
                                        ? lagenVorschau(lagen)
                                        : 'Angaben ungültig: Abstand 0,5–5 m, 1–40 Lagen.'}
                                </p>
                            </>
                        )}
                    </Abschnitt>

                    {/* ─── Pläne ────────────────────────────────────────── */}
                    <Abschnitt titel="Pläne" symbol={<PlanSymbol className="w-4 h-4 text-sky-600" />}>
                        <p className="text-xs text-slate-500 mt-0.5">
                            Geschossplan einem Geschoss zuordnen; Lage-/BE-Plan für draußen und das Gerüst. Mit Georeferenz setzt die
                            Kamera einen Pin aus GPS.
                        </p>
                        {plaene.length === 0 ? (
                            <p className="mt-2 text-xs text-slate-500 italic">Keine Pläne im Projekt — erst oben einen Plan hochladen.</p>
                        ) : (
                            <ul className="mt-2 space-y-2">
                                {planStatus.map(({ plan, k, geo }) => (
                                    <li key={plan.id} className="rounded-xl border border-slate-200 p-3">
                                        <div className="flex gap-3">
                                            <img
                                                src={plan.image}
                                                alt=""
                                                loading="lazy"
                                                className="w-16 h-12 shrink-0 rounded border border-slate-200 bg-slate-100 object-cover"
                                            />
                                            <div className="min-w-0 flex-1">
                                                <p className="text-sm font-semibold text-slate-800 truncate">{plan.name}</p>
                                                <GeoreferenzStatus ergebnis={geo} passpunkte={k?.passpunkte?.length ?? 0} />
                                            </div>
                                        </div>
                                        <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2">
                                            <select
                                                value={k?.art ?? ''}
                                                onChange={(e) => {
                                                    const art = e.target.value === 'geschoss' || e.target.value === 'lageplan' ? e.target.value : null;
                                                    planAendern(plan.id, planArtSetzen(k, art));
                                                }}
                                                disabled={!aendern}
                                                aria-label={`Art des Plans ${plan.name}`}
                                                className="rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white disabled:bg-slate-50"
                                            >
                                                <option value="">Art wählen …</option>
                                                <option value="geschoss">Geschossplan</option>
                                                <option value="lageplan">Lageplan / BE-Plan</option>
                                            </select>
                                            {k?.art === 'geschoss' ? (
                                                <select
                                                    value={k.geschossId ?? ''}
                                                    onChange={(e) => planAendern(plan.id, { ...k, geschossId: e.target.value || null })}
                                                    disabled={!aendern}
                                                    aria-label={`Geschoss des Plans ${plan.name}`}
                                                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white disabled:bg-slate-50"
                                                >
                                                    <option value="">Geschoss wählen …</option>
                                                    {verortung.geschosse.map((gs) => (
                                                        <option key={gs.id} value={gs.id}>{gs.name}</option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <span className="hidden sm:block" />
                                            )}
                                            <button
                                                type="button"
                                                onClick={() => setKalibrierPlanId(plan.id)}
                                                className="inline-flex items-center justify-center gap-2 rounded-lg border border-sky-600 text-sky-700 bg-white active:bg-sky-50 text-sm font-semibold px-3 py-2"
                                            >
                                                <Crosshair className="w-4 h-4" /> {aendern ? 'Kalibrieren' : 'Passpunkte ansehen'}
                                            </button>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </Abschnitt>
                </div>
            )}

            {kalibrierPortal}
        </section>
    );
}
